import { IonImg, IonSpinner } from '@ionic/react'
import { ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Divider } from '@mui/material'
import { SynerallContract } from '@vae/server/src/modules/ExternalAPIs/Synerall/synerallTypes'
import { FixedProductDetails } from '@vae/server/src/types/synerallApiTypes'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import React, { FC, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppContext } from '../../../contexts/AppContext'
import useUser from '../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import GetOurContract from '../../../modals/getOurContract/GetOurContract'
import ContractInfoItem from '../components/ContractInfoItem'
import LockInCard from '../components/LockInCard'
import { useProductDiscounts } from '../hooks/useProductDiscounts'

dayjs.extend(isBetween)

export interface ContractDetailsProps {
	contract?: SynerallContract | null
	contractLoading: boolean
}

const ContractDetails: FC<ContractDetailsProps> = ({ contract, contractLoading }) => {
	const { t } = useTranslation('pages_contractInformation')
	const { user } = useUser()

	const { isDarkMode } = useContext(AppContext)
	const { synerallContractId, price } = useContext(ObjectContext)

	const [expanded, setExpanded] = useState<string | false>(false)

	const handleAccordionChange =
		(panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
			setExpanded(isExpanded ? panel : false)
		}

	/**
	 * Function to get the contract type text.
	 * @returns {string} The contract type text.
	 */
	const getContractTypeText = () => {
		return price.contractType === 'spot' ? `${t('toistaiseksiVoimassa')}` : `${t('maaraaikainen')}`
	}

	const product = contract?.Products[0]

	const productName = useMemo(
		() => product?.ProductNames.find(product => product.Lang === 'FI')?.Value,
		[product]
	)

	const productId = product?.SupplyProductID ?? null
	const { remaining } = useProductDiscounts(productId, contract?.StartDate ?? null)

	if (!synerallContractId) return <GetOurContract />

	if (contractLoading) {
		return (
			<div id='contract-information'>
				<IonSpinner />
			</div>
		)
	}

	return (
		<div className='outer-container rounded-lg bg-white border border-brand-primary-100-light dark:border-brand-primary-100-dark dark:bg-background-200-dark'>
			<div className='container'>
				<img className='contract-image' src={'./assets/contract_bg.webp'} />

				<div className='info-container flex items-center gap-2'>
					<div className='info'>
						<p className='name dark:text-white'>{productName}</p>
						<p className='description text-brand-primary-100-light dark:text-brand-primary-100-dark'>
							{t('fossiilivapaa')}
						</p>
					</div>

					<IonImg
						className='h-20'
						src={isDarkMode ? './assets/minco2-dark.png' : './assets/minco2-light.png'}
					/>
				</div>

				<Divider />

				{price && (
					<div className='info-container2'>
						<div>
							<p className='header dark:text-white'>{t('price')}</p>
							{price.contractType === 'spot' ? (
								<p className='text dark:text-text-grey-100'>
									{(price.spotMargin ?? 0).toFixed(2).toString().replace('.', ',')} {t('spotUnit')}
								</p>
							) : (
								<p className='text dark:text-text-grey-100'>
									{price.value.toFixed(2).toString().replace('.', ',')} {t('priceUnit')}
								</p>
							)}
						</div>
					</div>
				)}

				<div className='info-container2'>
					<ContractInfoItem
						label={t('perusmaksu')}
						value={`${(price.monthlyPrice ?? 0).toFixed(2).toString().replace('.', ',')} ${t('monthlyUnit')}`}
					/>
					<ContractInfoItem label={t('sopimustyyppi')} value={getContractTypeText()} />
				</div>

				{(remaining.freeMonthlyFeeMonths > 0 || remaining.zeroMarginMonths > 0) && (
					<div className='info-container2'>
						{remaining.freeMonthlyFeeMonths > 0 && (
							<ContractInfoItem
								label={t('freeMonthlyFee')}
								value={`${remaining.freeMonthlyFeeMonths} ${t('months')}`}
							/>
						)}
						{remaining.zeroMarginMonths > 0 && (
							<ContractInfoItem
								label={t('zeroMargin')}
								value={`${remaining.zeroMarginMonths} ${t('months')}`}
							/>
						)}
					</div>
				)}

				<div className='info-container2'>
					{contract?.StartDate && (
						<ContractInfoItem
							label={t('alkamispaiva')}
							value={dayjs(contract.StartDate).format('DD.MM.YYYY')}
						/>
					)}
					{contract?.EndDate && (
						<ContractInfoItem
							label={t('loppumispaiva')}
							value={dayjs(contract.EndDate).format('DD.MM.YYYY')}
						/>
					)}
				</div>
			</div>

			{price.isLockInContract && (
				<>
					<div className='lock-ins-container'>
						<p className='header dark:text-white'>{t('upcoming')}</p>
						{price.upcomingLockIns.length ? (
							price.upcomingLockIns
								.sort((a, b) => new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime())
								.map((lockIn: FixedProductDetails) => (
									<Accordion
										key={lockIn.id}
										expanded={expanded === `panel-${lockIn.id}`}
										onChange={handleAccordionChange(`panel-${lockIn.id}`)}
									>
										<AccordionSummary expandIcon={<ExpandMore />}>
											{lockIn.texts[user!.language]?.title}
										</AccordionSummary>
										<AccordionDetails>
											<LockInCard details={lockIn} />
										</AccordionDetails>
									</Accordion>
								))
						) : (
							<p>{t('noUpcoming')}</p>
						)}
					</div>

					<Divider />

					<div className='lock-ins-container'>
						<p className='header text-text-dark dark:text-white'>{t('old')}</p>
						{price.oldLockIns.length > 0 ? (
							price.oldLockIns
								.sort((a, b) => new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime())
								.map((lockIn: FixedProductDetails) => (
									<Accordion
										key={lockIn.id}
										expanded={expanded === `panel-${lockIn.id}`}
										onChange={handleAccordionChange(`panel-${lockIn.id}`)}
									>
										<AccordionSummary expandIcon={<ExpandMore />}>
											{lockIn.texts[user!.language]?.title}
										</AccordionSummary>
										<AccordionDetails>
											<LockInCard details={lockIn} />
										</AccordionDetails>
									</Accordion>
								))
						) : (
							<p className='dark:text-white text-text-dark'>{t('noOld')}</p>
						)}
					</div>
				</>
			)}
		</div>
	)
}

export default ContractDetails
